/// DebugPanel: Main debug panel for the bottom panel area.
/// Provides debug controls, instance selector, breakpoints list,
/// step history, and animation speed control.
///
/// Phase 20, Plan 03.

import { useState, useMemo, useCallback } from "react";
import { Play, Pause, SkipForward, Square, Circle, Zap, X } from "lucide-react";
import { useDebugStore } from "@/stores/debugStore";
import { useEngineStore } from "@/stores/engineStore";
import { useDebugSession } from "@/hooks/useDebugSession";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugPanel() {
  const { startDebug, stopDebug, step, continueExecution, mode } = useDebugSession();

  const pauseReason = useDebugStore((s) => s.pauseReason);
  const breakpoints = useDebugStore((s) => s.breakpoints);
  const stepHistory = useDebugStore((s) => s.stepHistory);
  const animationSpeed = useDebugStore((s) => s.animationSpeed);
  const setAnimationSpeed = useDebugStore((s) => s.setAnimationSpeed);
  const removeBreakpoint = useDebugStore((s) => s.removeBreakpoint);
  const currentInstanceKey = useDebugStore((s) => s.currentInstanceKey);

  // Get running instances from engine store for the instance selector.
  // NOTE: Select the raw array (stable ref) and filter in useMemo.
  // Calling .filter() inside the selector creates a new array ref every snapshot,
  // which triggers the useSyncExternalStore infinite re-render loop.
  const allInstances = useEngineStore((s) => s.runningInstances);
  const runningInstances = useMemo(
    () => allInstances.filter((inst) => inst.state === "running"),
    [allInstances],
  );

  const [selectedInstanceKey, setSelectedInstanceKey] = useState<string>("");

  // Resolve which instance key to use for debug commands
  const effectiveInstanceKey = currentInstanceKey ?? selectedInstanceKey;

  // Button state
  const isIdle = mode === "idle";
  const isPaused = mode === "paused";
  const isRunning = mode === "running";
  const isStepping = mode === "stepping";

  const canPlay = isIdle ? !!selectedInstanceKey : isPaused;
  const canStep = isPaused && pauseReason !== "stopped";
  const canStop = !isIdle;

  // Breakpoint list as array
  const breakpointList = useMemo(() => Array.from(breakpoints.values()), [breakpoints]);

  // Step history (newest first, last 10)
  const recentHistory = useMemo(() => {
    const slice = stepHistory.slice(-10);
    return slice.reverse();
  }, [stepHistory]);

  // Handlers
  const handlePlay = useCallback(() => {
    if (isIdle && selectedInstanceKey) {
      startDebug(selectedInstanceKey);
    } else if (isPaused) {
      continueExecution();
    }
  }, [isIdle, isPaused, selectedInstanceKey, startDebug, continueExecution]);

  const handleStop = useCallback(() => {
    stopDebug();
    setSelectedInstanceKey("");
  }, [stopDebug]);

  return (
    <div data-testid="debug-panel" className="flex flex-col h-full overflow-hidden">
      {/* Debug controls row */}
      <div className="flex items-center gap-2 border-b border-zinc-700 px-3 py-1.5 shrink-0">
        {/* Play / Continue */}
        <button
          type="button"
          disabled={!canPlay}
          onClick={handlePlay}
          className={controlButtonClass(canPlay)}
          title={isIdle ? "Start Debug" : "Continue (F5)"}
        >
          {isRunning || isStepping ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Step */}
        <button
          type="button"
          disabled={!canStep}
          onClick={step}
          className={controlButtonClass(canStep)}
          title="Step Over (F10)"
        >
          <SkipForward className="h-3.5 w-3.5" />
        </button>

        {/* Stop */}
        <button
          type="button"
          disabled={!canStop}
          onClick={handleStop}
          className={controlButtonClass(canStop)}
          title="Stop Debug (Shift+F5)"
        >
          <Square className="h-3.5 w-3.5" />
        </button>

        <div className="h-4 w-px bg-zinc-700" />

        {/* Instance selector */}
        <select
          value={effectiveInstanceKey}
          onChange={(e) => setSelectedInstanceKey(e.target.value)}
          disabled={!isIdle}
          className="flex-1 max-w-[200px] bg-zinc-900 text-xs text-zinc-300 rounded px-2 py-1 border border-zinc-600 disabled:opacity-50"
        >
          <option value="">Select instance...</option>
          {runningInstances.map((inst) => (
            <option key={inst.instanceKey} value={inst.instanceKey}>
              {inst.processId} ({inst.instanceKey})
            </option>
          ))}
        </select>

        {/* Mode indicator */}
        <div className="flex items-center gap-1 text-xs text-zinc-500">
          <ModeIndicator mode={mode} />
          <span className="capitalize">
            {mode === "paused" && pauseReason === "breakpoint"
              ? "Breakpoint hit"
              : mode === "paused" && pauseReason === "stopped"
                ? "Paused"
                : mode}
          </span>
        </div>
      </div>

      {/* Main content: 3-column split */}
      <div className="flex-1 min-h-0 flex divide-x divide-zinc-700 overflow-hidden">
        {/* Breakpoints list */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="px-2 py-1 text-[10px] font-medium text-zinc-500 uppercase tracking-wider border-b border-zinc-700">
            Breakpoints ({breakpointList.length})
          </div>
          <div className="flex-1 overflow-y-auto">
            {breakpointList.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-600 text-center">No breakpoints set</div>
            ) : (
              breakpointList.map((bp) => (
                <div
                  key={bp.elementId}
                  className="flex items-center gap-1.5 px-2 py-1 hover:bg-zinc-800/50 group"
                >
                  {bp.condition ? (
                    <Zap className="h-3 w-3 text-yellow-500 shrink-0" />
                  ) : (
                    <Circle className="h-3 w-3 text-red-500 fill-red-500 shrink-0" />
                  )}
                  <span className="text-xs text-zinc-300 truncate flex-1">{bp.bpmnElementId}</span>
                  {bp.condition && (
                    <span className="text-[10px] text-yellow-500/60 truncate max-w-[80px]">
                      {bp.condition}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeBreakpoint(bp.elementId)}
                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-zinc-300 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Step history */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="px-2 py-1 text-[10px] font-medium text-zinc-500 uppercase tracking-wider border-b border-zinc-700">
            Step History
          </div>
          <div className="flex-1 overflow-y-auto font-mono">
            {recentHistory.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-600 text-center">No steps recorded</div>
            ) : (
              recentHistory.map((entry, i) => (
                <div
                  key={`${entry.timestamp}-${i}`}
                  className="flex items-start gap-1.5 px-2 py-0.5 hover:bg-zinc-800/50 text-[11px]"
                >
                  <span className="text-zinc-500 shrink-0">
                    {formatRelativeTime(entry.timestamp)}
                  </span>
                  <span className="text-zinc-300 truncate">{entry.elementId}</span>
                  <span className="text-zinc-600 truncate">
                    {summarizeVariables(entry.variables)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Animation speed */}
        <div className="w-36 flex flex-col shrink-0 overflow-hidden">
          <div className="px-2 py-1 text-[10px] font-medium text-zinc-500 uppercase tracking-wider border-b border-zinc-700">
            Speed
          </div>
          <div className="px-2 py-2 flex flex-col gap-2">
            <input
              type="range"
              min="0.25"
              max="4"
              step="0.25"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              className="w-full accent-blue-500"
            />
            <div className="text-center text-xs text-zinc-400">{animationSpeed.toFixed(2)}x</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function controlButtonClass(enabled: boolean): string {
  return [
    "inline-flex items-center justify-center h-6 w-6 rounded transition-colors",
    enabled
      ? "text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100"
      : "text-zinc-600 cursor-not-allowed",
  ].join(" ");
}

function ModeIndicator({ mode }: { mode: string }) {
  const colorClass =
    mode === "running"
      ? "bg-green-500"
      : mode === "paused"
        ? "bg-yellow-500"
        : mode === "stepping"
          ? "bg-red-500"
          : "bg-zinc-500";

  return <span className={`inline-block h-2 w-2 rounded-full ${colorClass}`} />;
}

function formatRelativeTime(timestamp: number): string {
  const diffSec = Math.floor((Date.now() - timestamp) / 1000);
  if (diffSec < 1) return "now";
  if (diffSec < 60) return `${diffSec}s`;
  return `${Math.floor(diffSec / 60)}m`;
}

function summarizeVariables(variables: Record<string, unknown>): string {
  const keys = Object.keys(variables);
  if (keys.length === 0) return "";
  const firstEntries = keys.slice(0, 2).map((k) => {
    const val = variables[k];
    if (typeof val === "string") return `${k}="${val.slice(0, 15)}"`;
    return `${k}=${String(val)}`;
  });
  return firstEntries.join(", ") + (keys.length > 2 ? "..." : "");
}
